﻿import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Card, CardContent } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { ProductionChartData } from "../models/MillOverview2";

type Props = {
	data: ProductionChartData[];
};

export const ProductionChart = ({ data }: Props) => {
	const chartOptions = useMemo(() => {
		const targetTPH = data.length > 0 ? (data[0].TargetTPH ?? 0) : 0;

		const actualData = data
			.filter((item) => item.Actual !== null)
			.map((item) => [item.Time, item.Actual]);

		const cumulativeAvgData = data
			.filter((item) => item.CumulativeAvg !== null)
			.map((item) => [item.Time, item.CumulativeAvg]);

		return {
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "cross",
					label: {
						show: false,
						formatter: function (params: any) {
							if (params.axisDimension === "x") {
								return dxtToLocalServerTime(params.value, "HH:mm") || "";
							} else {
								return params.value.toFixed(1);
							}
						},
					},
				},
				formatter: (params: any) => {
					const time = dxtToLocalServerTime(params[0].value[0], "HH:mm") || "";
					let result = `${time}<br/>`;
					params.forEach((param: any) => {
						result += `${param.marker} ${param.seriesName}: ${parseFloat(param.value[1].toFixed(3))}<br/>`;
					});
					return result;
				},
			},
			legend: {
				data: ["Target", "Actual", "Cumulative Avg"],
				top: 5,
				right: 10,
				textStyle: {
					fontSize: 10,
				},
			},
			grid: {
				left: "5%",
				right: "5%",
				bottom: "15%",
				top: "15%",
				containLabel: true,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					fontSize: 9,
					formatter: function (value: any) {
						return dxtToLocalServerTime(value, "HH:mm") || "";
					},
				},
				name: "Hours",
				nameLocation: "middle",
				nameGap: 25,
				nameTextStyle: { fontSize: 10 },
			},
			yAxis: {
				type: "value",
				name: "Tons Per Hour",
				nameLocation: "middle",
				nameGap: 35,
				nameTextStyle: {
					fontSize: 10,
				},
				axisLabel: {
					fontSize: 9,
				},
			},
			series: [
				{
					name: "Target",
					type: "line",
					data: data.map((item) => [item.Time, item.TargetTPH]),
					smooth: false,
					symbol: "none",
					lineStyle: {
						color: "#4CAF50",
						width: 2,
						type: "dashed",
					},
					itemStyle: {
						color: "#4CAF50",
					},
					markLine:
						targetTPH > 0
							? {
									silent: true,
									symbol: "none",
									label: { show: false },
									lineStyle: {
										color: "#4CAF50",
										type: "dashed",
									},
									data: [{ yAxis: targetTPH }],
								}
							: undefined,
				},
				{
					name: "Actual",
					type: "line",
					data: actualData,
					smooth: true,
					symbol: "none",
					areaStyle: {
						color: {
							type: "linear",
							x: 0,
							y: 0,
							x2: 0,
							y2: 1,
							colorStops: [
								{ offset: 0, color: "rgba(156, 39, 176, 0.15)" },
								{ offset: 1, color: "rgba(156, 39, 176, 0.02)" },
							],
						},
					},
					lineStyle: {
						color: "#9C27B0",
						width: 1,
						type: "dotted",
					},
					itemStyle: {
						color: "#9C27B0",
					},
				},
				{
					name: "Cumulative Avg",
					type: "line",
					data: cumulativeAvgData,
					smooth: true,
					symbol: "none",
					lineStyle: {
						color: "#2196F3",
						width: 3,
					},
					itemStyle: {
						color: "#2196F3",
					},
				},
			],
		};
	}, [data]);

	return (
		<Card elevation={1}>
			<CardContent sx={{ p: 1, "&:last-child": { pb: 0 } }}>
				<ReactECharts
					option={chartOptions}
					style={{ height: "230px", width: "100%" }}
					opts={{ renderer: "svg" }}
				/>
			</CardContent>
		</Card>
	);
};
